import { useEffect, useRef, useState } from "react"

import mapboxgl from "mapbox-gl"
import { MAPBOX_CONFIG } from "web/constants"

import { isObjectWithProperty } from "web/services/helpers"

import pinImage from "web/images/map-pin.png"
import { defaultPinColour } from "../Map/common"

import MapButton from "../Map/MapButton"

const MAP_STYLE_STREET = "streets-v12"
const MAP_STYLE_SATELITE = "satellite-streets-v12"
const MAP_CENTER = [0, 0]

mapboxgl.accessToken = MAPBOX_CONFIG.accessToken

export default function LocationSelectionMap({
  location,
  onSelect,
  zoomButtons = false,
}) {
  const [map, setMap] = useState(null)
  const [mapStyle, setMapStyle] = useState(MAP_STYLE_STREET)

  const mapId = "location-selection-map"

  const locationHasLatLong = isObjectWithProperty(location, "longitude")

  const locationLngLat = locationHasLatLong
    ? [location.longitude, location.latitude]
    : MAP_CENTER

  const locationDataRef = useRef()

  locationDataRef.current = {
    type: "FeatureCollection",
    features: locationHasLatLong
      ? [
          {
            type: "Feature",
            properties: { icon: "pin" },
            geometry: {
              type: "Point",
              coordinates: locationLngLat,
            },
          },
        ]
      : [],
  }

  function createMap() {
    const zoom = map?.getZoom() || 1
    const center = map ? Object.values(map.getCenter()) : locationLngLat

    const _map = new mapboxgl.Map({
      container: mapId,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      zoom: zoom,
      center: center,
    })

    _map.on("load", () => {
      _map.loadImage(pinImage, (error, image) => {
        if (error) throw error

        _map.addImage("pin", image, { sdf: true })

        _map.addSource("location", {
          type: "geojson",
          data: locationDataRef.current,
        })

        _map.addLayer({
          id: "location",
          type: "symbol",
          source: "location",
          layout: {
            "icon-image": ["get", "icon"],
            "icon-offset": [0, -24],
            "icon-allow-overlap": true,
          },
          paint: {
            "icon-color": defaultPinColour,
            "icon-halo-color": "white",
            "icon-halo-width": 3,
          },
        })
      })
    })

    _map.on("click", function (e) {
      e.preventDefault()
      onSelect({ longitude: e.lngLat.lng, latitude: e.lngLat.lat })
    })

    // hide mapbox logo
    const logo = document.getElementsByClassName("mapboxgl-ctrl-logo")

    for (const el of logo) {
      el.style.display = "none"
    }

    const attrib = document.getElementsByClassName("mapboxgl-ctrl-attrib")

    for (const el of attrib) {
      el.style.display = "none"
    }

    setMap(_map)
  }

  function updateMap() {
    const mapSource = map.getSource("location")

    if (mapSource) {
      mapSource.setData(locationDataRef.current)

      if (locationHasLatLong) {
        map.flyTo({ center: locationLngLat, zoom: 15 })
      }
    }
  }

  useEffect(() => {
    createMap()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle])

  useEffect(() => {
    if (map) {
      updateMap()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  function toggleMapStyle() {
    const newStyle =
      mapStyle === MAP_STYLE_STREET ? MAP_STYLE_SATELITE : MAP_STYLE_STREET

    setMapStyle(newStyle)
  }

  return (
    <div className="map-container h-full">
      <div className="box-border h-full w-full" id={mapId} />

      <div className="absolute bottom-4 left-4 z-10 flex space-x-2">
        <MapButton
          name={mapStyle === MAP_STYLE_STREET ? "map" : "globe-americas"}
          onClick={toggleMapStyle}
        />
        {zoomButtons && (
          <>
            <MapButton
              name="magnifying-glass-plus"
              onClick={() => map.zoomIn()}
            />
            <MapButton
              name="magnifying-glass-minus"
              onClick={() => map.zoomOut()}
            />
          </>
        )}
      </div>
    </div>
  )
}
